/**
 * Home page story feed: primary-tag filter (synced to ?tag=) and "load more" paging
 */
const PAGE_SIZE = 8;
const ALL_TAGS = 'all';
const ANIMATION = { duration: 450, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' };

export default function postFeed() {
    const section = document.querySelector('[data-feed]');
    if (!section) return;

    const list = section.querySelector('[data-feed-list]');
    const tagNav = section.querySelector('[data-feed-tags]');
    const empty = section.querySelector('[data-feed-empty]');
    const footer = section.querySelector('[data-feed-footer]');
    const count = section.querySelector('[data-feed-count]');
    const more = section.querySelector('[data-feed-more]');

    const rows = [...list.querySelectorAll('.gh-row')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const exiting = new Set();

    let activeTag = new URLSearchParams(window.location.search).get('tag') || ALL_TAGS;
    let visibleCount = PAGE_SIZE;

    const allButton = tagNav.querySelector('[data-filter]');
    const tags = new Map();
    rows.forEach((row) => row.dataset.tag && tags.set(row.dataset.tag, row.dataset.tagName));
    tags.forEach((name, slug) => {
        const button = allButton.cloneNode();
        button.dataset.filter = slug;
        button.textContent = name;
        tagNav.append(button);
    });
    const tagButtons = [...tagNav.querySelectorAll('[data-filter]')];

    // The newest post is featured above the list, so it only reappears once the reader picks a tag
    const listedRows = () =>
        activeTag === ALL_TAGS
            ? rows.filter((row) => !row.hasAttribute('data-featured'))
            : rows.filter((row) => row.dataset.tag === activeTag);

    const finishExit = (row) => {
        row.getAnimations().forEach((animation) => animation.cancel());
        row.removeAttribute('style');
        row.hidden = true;
        exiting.delete(row);
    };

    const render = ({ animate = true } = {}) => {
        exiting.forEach(finishExit);

        const listed = listedRows();
        const visible = new Set(listed.slice(0, visibleCount));
        const before = new Map(rows.filter((row) => !row.hidden).map((row) => [row, row.getBoundingClientRect()]));

        rows.forEach((row) => {
            row.hidden = !visible.has(row);
        });

        tagButtons.forEach((button) => {
            const isActive = button.dataset.filter === activeTag;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
        });
        empty.hidden = listed.length > 0;
        footer.hidden = listed.length === 0;
        count.textContent = `Showing ${visible.size} of ${listed.length} stories`;
        more.hidden = visible.size >= listed.length;

        if (animate && !reduceMotion) animateChanges(before);
    };

    const animateChanges = (before) => {
        const listRect = list.getBoundingClientRect();

        rows.forEach((row) => {
            const old = before.get(row);

            if (!row.hidden && !old) {
                row.animate(
                    [
                        { opacity: 0, transform: 'translateY(24px) scale(0.97)' },
                        { opacity: 1, transform: 'translateY(0) scale(1)' },
                    ],
                    ANIMATION
                );
            } else if (!row.hidden) {
                const now = row.getBoundingClientRect();
                const deltaX = old.left - now.left;
                const deltaY = old.top - now.top;
                if (deltaX || deltaY) {
                    row.animate(
                        [{ transform: `translate(${deltaX}px, ${deltaY}px)` }, { transform: 'translate(0, 0)' }],
                        ANIMATION
                    );
                }
            } else if (old) {
                // Keep the removed row at its old spot, out of the layout flow, while it fades out
                row.hidden = false;
                Object.assign(row.style, {
                    position: 'absolute',
                    top: `${old.top - listRect.top}px`,
                    left: `${old.left - listRect.left}px`,
                    width: `${old.width}px`,
                    pointerEvents: 'none',
                });
                exiting.add(row);
                row.animate(
                    [
                        { opacity: 1, transform: 'translateY(0) scale(1)' },
                        { opacity: 0, transform: 'translateY(-12px) scale(0.98)' },
                    ],
                    ANIMATION
                ).onfinish = () => finishExit(row);
            }
        });
    };

    const setTag = (slug) => {
        activeTag = slug;
        const url = new URL(window.location.href);
        if (slug === ALL_TAGS) {
            url.searchParams.delete('tag');
        } else {
            url.searchParams.set('tag', slug);
        }
        window.history.replaceState(window.history.state, '', url);
    };

    tagNav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-filter]');
        if (!button) return;
        setTag(button.dataset.filter);
        render();
    });

    section.querySelector('[data-feed-clear]').addEventListener('click', () => {
        setTag(ALL_TAGS);
        render();
    });

    more.addEventListener('click', () => {
        visibleCount += PAGE_SIZE;
        render();
    });

    render({ animate: false });
}
